"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Facebook, Instagram, Twitter, MapPin, Phone, Mail, Clock, CreditCard } from "lucide-react";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-32 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="flex flex-col gap-3">
        {[{
        label: "Home",
        href: "/HomePage"
      }, {
        label: "About AIB",
        href: "/AboutAIBPage"
      }, {
        label: "Methodology",
        href: "/MethodologyPage"
      }, {
        label: "Media Centre",
        href: "/MediaCentrePage"
      }].map((item, i) => <li key={i} data-index={i}>
            <Link to={item.href} className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm font-medium">
              {item.label}
            </Link>
          </li>)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm font-medium">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function InformativeFooter() {
  const currentYear = new Date().getFullYear();
  return <section data-section-id="252794" id="footer" className="bg-background text-foreground pt-16 pb-8 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          <div className="flex flex-col gap-6">
            <Link to="/" className="inline-block w-fit">
              <WvcLogo className="h-16 w-auto text-primary" />
            </Link>
            <div className="flex items-center gap-4 mt-2">
              <Link to="#" className="bg-muted/50 p-2 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-200">
                <Facebook className="size-5" />
                <span className="sr-only">Facebook</span>
              </Link>
              <Link to="#" className="bg-muted/50 p-2 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-200">
                <Instagram className="size-5" />
                <span className="sr-only">Instagram</span>
              </Link>
              <Link to="#" className="bg-muted/50 p-2 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-200">
                <Twitter className="size-5" />
                <span className="sr-only">Twitter</span>
              </Link>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold font-default text-foreground tracking-tight">Contact</h3>
            <ul className="flex flex-col gap-4">
              <li>
                <Link to="tel:1300000AIB" className="flex items-start gap-3 text-muted-foreground hover:text-primary transition-colors duration-200 group">
                  <Phone className="size-5 shrink-0 mt-0.5 group-hover:text-primary" />
                  <span className="text-sm font-default">1300 000 AIB</span>
                </Link>
              </li>
              <li>
                <Link to="mailto:enquiries@aibureau.org.au" className="flex items-start gap-3 text-muted-foreground hover:text-primary transition-colors duration-200 group">
                  <Mail className="size-5 shrink-0 mt-0.5 group-hover:text-primary" />
                  <span className="text-sm font-default">enquiries@aibureau.org.au</span>
                </Link>
              </li>
              <li className="flex items-start gap-3 text-muted-foreground">
                <MapPin className="size-5 shrink-0 mt-0.5" />
                <span className="text-sm font-default">Level 12, 100 Collins St, Melbourne VIC 3000</span>
              </li>
              <li className="flex items-start gap-3 text-muted-foreground">
                <Clock className="size-5 shrink-0 mt-0.5" />
                <span className="text-sm font-default">Mon - Fri: 8:30am - 5:30pm AEST</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold font-default text-foreground tracking-tight">Bureau Indices</h3>
            <MenuProvider menu_id="55">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold font-default text-foreground tracking-tight">Resources</h3>
            <ul className="flex flex-col gap-3">
              {[{
              label: "Methodology Framework",
              href: "/MethodologyPage"
            }, {
              label: "Media Centre",
              href: "/MediaCentrePage"
            }, {
              label: "Privacy Policy",
              href: "#"
            }, {
              label: "Terms of Use",
              href: "#"
            }, {
              label: "Data Licensing",
              href: "#"
            }].map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary transition-colors duration-200 text-sm font-medium">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

        </div>
      </div>

      <div className="border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left font-default">
              &copy; {currentYear} The Australian Index Bureau. All rights reserved.
            </p>
            <div className="flex items-center gap-3 text-muted-foreground">
              <CreditCard className="size-6 opacity-50 hover:opacity-100 transition-opacity" />
              <div className="h-6 w-10 border border-border rounded-sm flex items-center justify-center text-[10px] font-bold font-mono opacity-50 hover:opacity-100 transition-opacity cursor-default">VISA</div>
              <div className="h-6 w-10 border border-border rounded-sm flex items-center justify-center text-[10px] font-bold font-mono opacity-50 hover:opacity-100 transition-opacity cursor-default">AMEX</div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}